import { useEventContext, useQuizContext, useQuizSessionContext } from 'contexts';
import { CSSProperties, useState, useCallback, useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { StartScreen } from '../../slideshow-quiz/start-screen';
import { QuestionSlides } from '../../slideshow-quiz/question-slides';

import '../../slideshow-quiz/slideshow-quiz.css';
import { useQuery } from '@apollo/client';
import { getQuizQuestions } from 'graphql/queries';
import { QuizQuestions, QuizQuestionsVariables } from 'graphql/queries/types/QuizQuestions';
import { QuizEndScreen } from '../../slideshow-quiz/quiz-end-sceen';
import { AnswerSlides } from '../../slideshow-quiz/answer-slides';
import { NavPages, NavUtils } from 'utils';
import omit from 'lodash/omit';
import { SessionTypes } from 'consts';
import { useSessionProgress } from 'hooks';
import { HoverMenu } from 'components';
import { Dialog, DialogTitle, DialogContent, Link } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { Box, Stack } from '@mui/system';

enum Scenes {
    Title,
    PresentationQuiz,
    QuizEndScreen,
    AnswersPresentation
}

interface Props {}

/**
 * Used to present the quiz on a projector in live quizzes
 * TODOs:
 * * DONE extend QuesionSlides leverage current question index and update session progress
 * * DONE add route path to this component inside the session route
 * * DONE add route path to quick answer componnset inside session route
 * * TODO test if the hook for session progress works as expected
 * * DONE Add option to pause/unpause quiz - add this to the hover menu at the top of the slide
 * * DONE Pause quiz automatically when going to a previous slide
 * @param props
 * @returns
 */
export const PresentQuizForQuickAnswer = (props: Props) => {
    const { quiz } = useQuizContext();
    const session = useQuizSessionContext();
    const event = useEventContext();
    const params = useParams();
    const sessionProgressActions = useSessionProgress(session?.session.id!);
    const navigate = useNavigate();
    const quizId = parseInt(params.quizId!);
    const [currentScene, setCurrentScene] = useState(Scenes.Title);
    const [showSessionQrCode, setShowSessionQrCode] = useState(false);

    useEffect(() => {
        if (!!sessionProgressActions.sessionProgress && (sessionProgressActions.sessionProgress.progressIndex ?? -1) > -1) {
            setCurrentScene(Scenes.PresentationQuiz);
        }
    }, [sessionProgressActions.sessionProgress]);

    const onStartClick = () => {
        sessionProgressActions.updateSessionProgress({
            id: sessionProgressActions.sessionProgress?.id!,
            is_paused: false,
            progress_index: 0
        });
        setCurrentScene(Scenes.PresentationQuiz);
    };

    const onFinishQuiz = () => {
        setCurrentScene(Scenes.QuizEndScreen);
        sessionProgressActions.closeAllQuizUserSessions();
    };

    const onShowAnswers = () => {
        setCurrentScene(Scenes.AnswersPresentation);
    };

    const onEndQuiz = () => {
        navigate(NavUtils.getQuizSelectorPath(event?.alias));
    };

    const onSlideIndexChange = useCallback(
        (currentIndex: number) => {
            sessionProgressActions.updateSessionProgress({
                id: sessionProgressActions.sessionProgress?.id!,
                is_paused: (sessionProgressActions?.sessionProgress?.progressIndex ?? 0) > currentIndex,
                progress_index: currentIndex
            });
        },
        [sessionProgressActions]
    );

    const onPauseClick = useCallback(() => {
        sessionProgressActions.updateSessionProgress({
            id: sessionProgressActions.sessionProgress?.id!,
            is_paused: true,
            progress_index: sessionProgressActions?.sessionProgress?.progressIndex
        });
    }, [sessionProgressActions]);

    const onPlayClick = useCallback(() => {
        sessionProgressActions.updateSessionProgress({
            id: sessionProgressActions.sessionProgress?.id!,
            is_paused: false,
            progress_index: sessionProgressActions?.sessionProgress?.progressIndex
        });
    }, [sessionProgressActions]);

    const slideCustomFontStyles: CSSProperties = {
        ...quiz.styles?.fontStyles
    };

    const slideCustomBackgroundStyles: CSSProperties = {
        backgroundImage: quiz.styles?.backgroundStyles?.backgroundImageUrl
            ? `url("${quiz.styles?.backgroundStyles?.backgroundImageUrl}")`
            : undefined,
        ...omit(quiz.styles?.backgroundStyles, 'backgroundImageUrl')
    };

    const customTitlePageStyles: CSSProperties = {
        backgroundImage: quiz.styles?.titlePageStyles?.backgroundImageUrl
            ? `url("${quiz.styles?.titlePageStyles?.backgroundImageUrl}")`
            : undefined,
        ...omit(quiz.styles?.titlePageStyles, 'title', 'backgroundImageUrl')
    };
    const customTitleStyles: CSSProperties = {
        ...quiz.styles?.titlePageStyles?.title
    };

    const {
        loading: loadingQuizQuestions,
        // error,
        data: quizQuestions
        // refetch
    } = useQuery<QuizQuestions, QuizQuestionsVariables>(getQuizQuestions, {
        variables: { quizId: quizId }
    });

    if (session?.session.type !== SessionTypes.LivePaperless && session?.session.type !== SessionTypes.LiveQuickAnswer) {
        console.log('Session value', session);
        return <Navigate to={NavUtils.getQuizSelectorPath(event?.alias)} />;
    }

    const handleCloseQrCodeDialog = () => {
        setShowSessionQrCode(false);
    };

    const showQrCodeDialog = () => {
        setShowSessionQrCode(true);
    };

    const renderSessionQrCodeDialog = () => {
        const playerLink = NavUtils.generateAbsolutePath(
            NavPages.QuizSession,
            quiz.id,
            event?.alias,
            session.session.key
        );
        return (
            <Dialog
                open={showSessionQrCode}
                onClose={handleCloseQrCodeDialog}
                scroll="paper"
                maxWidth={false}
                aria-labelledby="session-qr-code-dialog-title"
            >
                <DialogTitle id="session-qr-code-dialog-title">Player link QR Code</DialogTitle>
                <DialogContent dividers={true}>
                    <Stack spacing={5}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                            <QRCodeSVG size={300} value={playerLink} />
                        </Box>
                        <Link href={playerLink} sx={{ typography: 'h4' }}>
                            {playerLink}
                        </Link>
                    </Stack>
                </DialogContent>
            </Dialog>
        );
    };

    const currentSlideIndex =
        sessionProgressActions.sessionProgress?.progressIndex && sessionProgressActions.sessionProgress?.progressIndex > -1
            ? sessionProgressActions.sessionProgress?.progressIndex
            : undefined;

    const scenes = {
        [Scenes.Title]: () => (
            <StartScreen
                onStartClick={onStartClick}
                onShowParticipantQrCode={showQrCodeDialog}
                backgroundStyles={customTitlePageStyles}
                fontStyles={slideCustomFontStyles}
                titleStyles={customTitleStyles}
            />
        ),
        [Scenes.PresentationQuiz]: () => (
            <>
                {!!quizQuestions && !loadingQuizQuestions && (
                    <QuestionSlides
                        quizQuestions={quizQuestions!}
                        eventLogoUrl={event?.logo_url}
                        finishQuiz={onFinishQuiz}
                        backgroundStyles={slideCustomBackgroundStyles}
                        fontStyles={slideCustomFontStyles}
                        onSlideIndexChange={onSlideIndexChange}
                        startingSlideIndex={currentSlideIndex}
                        hoverMenu={
                            <HoverMenu
                                onCloseClick={onEndQuiz}
                                onPauseClick={onPauseClick}
                                onPlayClick={onPlayClick}
                                onShowPlayerLink={showQrCodeDialog}
                                isPaused={sessionProgressActions?.sessionProgress?.isPaused}
                            />
                        }
                    />
                )}
            </>
        ),
        [Scenes.QuizEndScreen]: () => (
            <QuizEndScreen
                onShowAnswersClick={onShowAnswers}
                onEndQuiz={onEndQuiz}
                backgroundStyles={slideCustomBackgroundStyles}
                fontStyles={slideCustomFontStyles}
                titleStyles={customTitleStyles}
            />
        ),
        [Scenes.AnswersPresentation]: () => (
            <AnswerSlides
                quizQuestions={quizQuestions!}
                eventLogoUrl={event?.logo_url}
                finishQuiz={onFinishQuiz}
                backgroundStyles={slideCustomBackgroundStyles}
                fontStyles={slideCustomFontStyles}
            />
        )
    };

    return (
        <div className="aq-content">
            {!!sessionProgressActions.sessionProgress && !loadingQuizQuestions && scenes[currentScene]()}
            {renderSessionQrCodeDialog()}
        </div>
    );
};
